/**
 * Guest Login Screen - confirm the display-only guest nickname before auth.
 */

import { useState } from "react";
import { navigateTo } from "../../router";
import { Alert, Button, Card, Input } from "../../ui";
import { getSavedGuestNickname } from "../auth/guest-identity";
import {
  GUEST_NICKNAME_MAX_LENGTH,
  generateGuestNickname,
  getGuestNicknameLength,
  normalizeGuestNickname,
  validateGuestNickname,
} from "../auth/guest-nickname";
import { useAuth, useAuthActions } from "../store/auth-store";

function getInitialGuestNickname(): string {
  const savedNickname = getSavedGuestNickname();
  if (savedNickname && validateGuestNickname(savedNickname) === null) {
    return savedNickname;
  }
  return generateGuestNickname();
}

export function GuestLoginScreen() {
  const { isLoading, error } = useAuth();
  const actions = useAuthActions();

  const [nickname, setNickname] = useState(getInitialGuestNickname);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleNicknameChange = (value: string) => {
    setNickname(value);
    if (validationError) {
      setValidationError(validateGuestNickname(value));
    }
  };

  const regenerateNickname = () => {
    const nextNickname = generateGuestNickname();
    setNickname(nextNickname);
    setValidationError(null);
  };

  const handleGuestLogin = async () => {
    const nextError = validateGuestNickname(nickname);
    if (nextError) {
      setValidationError(nextError);
      return;
    }

    const success = await actions.loginAsGuest(
      normalizeGuestNickname(nickname),
    );
    if (success) {
      navigateTo("/");
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-3 sm:p-6">
      <Card className="w-full shadow-lg sm:max-w-md sm:p-8">
        <h1 className="mb-2 text-center font-semibold text-2xl">게스트 참여</h1>
        <p className="mb-6 text-center text-sm text-text-secondary">
          게임에서 보일 닉네임을 확인하세요
        </p>

        <Alert variant="info" className="mb-4">
          자동 생성된 닉네임을 그대로 쓰거나 수정할 수 있습니다.
        </Alert>

        {(validationError || error) && (
          <Alert className="mb-4">{validationError ?? error}</Alert>
        )}

        <div className="flex flex-col gap-3">
          <div>
            <label
              htmlFor="guest-nickname"
              className="mb-1 block font-semibold text-sm text-text-secondary"
            >
              닉네임
            </label>
            <div className="flex gap-2">
              <Input
                id="guest-nickname"
                type="text"
                data-testid="guest-nickname-input"
                value={nickname}
                maxLength={GUEST_NICKNAME_MAX_LENGTH}
                onChange={(event) => handleNicknameChange(event.target.value)}
                disabled={isLoading}
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                data-testid="guest-random-name-btn"
                onClick={regenerateNickname}
                disabled={isLoading}
                className="shrink-0"
              >
                랜덤
              </Button>
            </div>
            <div className="mt-1 flex justify-between gap-2 text-text-muted text-xs">
              <span>한글 자모, 영문, 숫자, -, _만 가능</span>
              <span data-testid="guest-nickname-count">
                {getGuestNicknameLength(nickname)} / {GUEST_NICKNAME_MAX_LENGTH}
              </span>
            </div>
          </div>

          <Button
            variant="success"
            data-testid="guest-submit-btn"
            onClick={() => void handleGuestLogin()}
            disabled={isLoading}
          >
            {isLoading ? "참여 중..." : "게스트 참여"}
          </Button>
        </div>

        <p className="mt-6 text-center text-sm text-text-secondary">
          <Button
            variant="ghost"
            size="sm"
            data-testid="guest-login-back-btn"
            onClick={() => navigateTo("/login")}
            className="px-0 py-0"
            disabled={isLoading}
          >
            이전으로
          </Button>
        </p>
      </Card>
    </div>
  );
}
